/**
 * The one rule for whether an org may use licensed Studio (M4S-5669).
 *
 * Licensed Studio is every page that is not Home, Account, Connect, a DANI page or internal:
 * Builder, Status, Videos, Crosstabs, My Work, Dashboards, Board Reports, plus the Sigma
 * dashboards and client self-serve Feasibility. Every place that decides one of those asks this
 * function; `studio-entitlement.guard.test.ts` fails on a new direct read of the license.
 *
 * The rule is "a valid Studio license OR a paid credits plan" (docs/pdlc/license-to-credits.md
 * §4, the safe defaults): every current license keeps working until its contract ends, and a
 * client on credits gets in too.
 *
 * - `isLicenseValid` is the legacy Studio license (`atlas.org_license`), sent by the API.
 * - `hasPaidCreditsPlan` is sent by the API once Jon E's change ships: an annual credits contract
 *   that has not ended, or a credit pack. A DANI trial is not paid, and nor is an "unlimited"
 *   account until someone decides it is. Until the API sends it, it is missing, so this answers
 *   exactly as the license alone did. The same API change must teach `LicenseGuard` the same OR,
 *   so Studio never opens a page whose calls the API still refuses.
 *
 * Never use `hasClientStudioAIAccess` as the paid signal: it is true for DANI trials too.
 * No imports and no `'use client'`: server route handlers and client hooks share it.
 */

/** The org fields the rule reads: the session org, the View-as org and the route rules' org all fit. */
export interface StudioEntitlementOrg {
    isLicenseValid?: boolean | null;
    hasPaidCreditsPlan?: boolean | null;
}

/** True when the org may use licensed Studio: a valid license, or a paid credits plan. */
export function hasStudioEntitlement(org: StudioEntitlementOrg | null | undefined): boolean {
    return Boolean(org?.isLicenseValid) || org?.hasPaidCreditsPlan === true;
}
